import { useEffect, useRef, useState } from "react";
import { Camera, BellRinging, CheckCircle, DeviceMobile, Lock } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { subscribeToPush, isSubscribed, pushSupported } from "@/lib/push";

const ROLE_LABEL = { player: "Player", admin: "Admin", primary_admin: "Primary Admin" };

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [nickname, setNickname] = useState(user?.nickname || "");
  const [saving, setSaving] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [installHint, setInstallHint] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { isSubscribed().then(setSubscribed); }, []);

  const saveName = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", { name: name.trim(), nickname: nickname.trim() });
      await refresh();
      toast.success("Profile updated");
    } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setSubscribed(true);
      toast.success("Reminders enabled on this device");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="animate-fade-up max-w-lg">
      <h1 className="font-heading font-black text-4xl uppercase tracking-tighter mb-6">Profile</h1>

      <div className="border border-border bg-card rounded-sm p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar name={user.name} path={user.avatar_path} size={80} />
            <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-button" className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-sm flex items-center justify-center border-2 border-card">
              <Camera size={16} weight="fill" className="text-white" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="avatar-file-input" />
          </div>
          <div>
            <div className="font-heading font-bold text-2xl uppercase tracking-tight">{user.nickname || user.name}</div>
            {user.nickname && <div className="text-sm text-muted-foreground">{user.name}</div>}
            <span className="text-xs font-bold uppercase tracking-widest text-primary">{ROLE_LABEL[user.role]}</span>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Nickname / Screen Name</label>
            <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. AcesHigh (shown on leaderboard & chat)" className="bg-secondary border-border h-11 mt-1.5" data-testid="nickname-input" />
            <p className="text-xs text-muted-foreground mt-1">Leave blank to show your first name + last initial.</p>
          </div>
          <div>
            <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Full Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-secondary border-border h-11 mt-1.5" data-testid="name-input" />
          </div>
          <Button onClick={saveName} disabled={saving} data-testid="save-name" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-11 w-full">Save Profile</Button>
          {user.email && <Field label="Email" value={user.email} />}
          {user.phone && <Field label="Phone" value={user.phone} />}
        </div>
      </div>

      {/* Notifications */}
      <div className="border border-border bg-card rounded-sm p-6 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <BellRinging size={22} weight="fill" className="text-primary" />
          <h2 className="font-heading font-bold text-xl uppercase tracking-tight">Notifications</h2>
        </div>
        {subscribed ? (
          <div className="flex items-center gap-2 text-[#34C759] font-semibold" data-testid="push-enabled">
            <CheckCircle size={20} weight="fill" /> Reminders are enabled on this device
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">Get push reminders for upcoming games and leaderboard updates.</p>
            <Button onClick={enablePush} disabled={!pushSupported()} data-testid="enable-push" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest">
              {pushSupported() ? "Enable Reminders" : "Not supported here"}
            </Button>
          </>
        )}

        <NotificationPrefs />

        <button onClick={() => setInstallHint(!installHint)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white mt-4" data-testid="install-hint-toggle">
          <DeviceMobile size={16} weight="bold" /> How to add to home screen
        </button>
        {installHint && (
          <div className="text-sm text-muted-foreground mt-2 space-y-1 border-l-2 border-primary/40 pl-3">
            <p><b className="text-white">iPhone:</b> Share button → “Add to Home Screen”.</p>
            <p><b className="text-white">Android:</b> Menu (⋮) → “Install app” / “Add to Home screen”.</p>
            <p className="text-xs">Push reminders on iPhone require installing to the home screen first.</p>
          </div>
        )}
      </div>

      {/* Change password */}
      <ChangePassword />

      <button onClick={logout} className="text-kinetic font-bold uppercase tracking-widest text-sm mt-6" data-testid="profile-logout">Log out</button>
    </div>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-border bg-card rounded-sm p-6 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={22} weight="fill" className="text-primary" />
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight">Change Password</h2>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Current Password</label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="bg-secondary border-border h-11 mt-1.5" data-testid="current-password-input" placeholder="••••••••" />
        </div>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">New Password</label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="bg-secondary border-border h-11 mt-1.5" data-testid="new-password-input" placeholder="At least 6 characters" />
        </div>
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Confirm New Password</label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-secondary border-border h-11 mt-1.5" data-testid="confirm-password-input" placeholder="Re-enter new password" />
        </div>
        <Button onClick={save} disabled={saving || !current || !next} data-testid="change-password-submit" className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-widest">
          {saving ? "Saving…" : "Update Password"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">{label}</label>
      <div className="bg-secondary border border-border rounded-sm px-3 h-11 flex items-center text-muted-foreground mt-1.5">{value}</div>
    </div>
  );
}

const PREF_ITEMS = [
  { key: "messages", label: "Chat messages", desc: "When someone posts in the group chat" },
  { key: "cards", label: "Yellow / red cards", desc: "Cards and suspensions" },
  { key: "reminders", label: "Upcoming game reminders", desc: "Before each scheduled game" },
  { key: "results", label: "Game results", desc: "1st, 2nd, 3rd after each game" },
  { key: "tournament", label: "Tournament champion", desc: "Final winners when it wraps up" },
];

function NotificationPrefs() {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => { api.get("/notifications/prefs").then((r) => setPrefs(r.data)); }, []);

  const toggle = async (key, val) => {
    setPrefs((p) => ({ ...p, [key]: val }));
    try {
      await api.patch("/notifications/prefs", { [key]: val });
    } catch {
      setPrefs((p) => ({ ...p, [key]: !val }));
      toast.error("Could not update");
    }
  };

  if (!prefs) return null;
  return (
    <div className="mt-5 border-t border-border pt-4">
      <div className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Manage notifications</div>
      <div className="space-y-3">
        {PREF_ITEMS.map((it) => (
          <div key={it.key} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm">{it.label}</div>
              <div className="text-xs text-muted-foreground">{it.desc}</div>
            </div>
            <Switch checked={prefs[it.key] !== false} onCheckedChange={(v) => toggle(it.key, v)} data-testid={`pref-${it.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
